import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Bug, Lightbulb, HelpCircle, Sparkles, Palette, MessageCircle, Camera, X, Trash2, MessageSquarePlus, Loader2, CheckCircle } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { trackEvent } from '../../lib/telemetry';

/** Window event that opens the feedback panel (the phone header's Feedback button sends it). */
export const OPEN_FEEDBACK_EVENT = 'sec:open-feedback';

const TYPES = [
  { value: 'bug', label: 'Bug', icon: Bug },
  { value: 'suggestion', label: 'Suggestion', icon: Lightbulb },
  { value: 'question', label: 'Question', icon: HelpCircle },
  { value: 'feature_request', label: 'Feature request', icon: Sparkles },
  { value: 'design_request', label: 'Design request', icon: Palette },
  { value: 'feel_request', label: 'Feels off', icon: MessageCircle }
];

const SEVERITIES = [
  { value: 'blocking', label: 'Blocking — I cannot work' },
  { value: 'critical', label: 'Critical' },
  { value: 'high', label: 'High' },
  { value: 'normal', label: 'Normal' },
  { value: 'enhancement', label: 'Enhancement / nice-to-have' }
];

const TIMEFRAMES = [
  { value: 'immediately', label: 'Immediately' },
  { value: 'within_a_few_days', label: 'Within a few days' },
  { value: 'within_a_week', label: 'Within a week' },
  { value: 'within_a_month', label: 'Within a month' }
];

function FeedbackWidget() {
  const location = useLocation();
  const { authFetch } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  // The phone header's Feedback button opens this panel (OPEN_FEEDBACK_EVENT): on a phone the floating
  // button sat over the page's own words and buttons (BH demo 390 sweep, 11-01).
  useEffect(() => {
    const open = () => setIsOpen(true);
    window.addEventListener(OPEN_FEEDBACK_EVENT, open);
    return () => window.removeEventListener(OPEN_FEEDBACK_EVENT, open);
  }, []);
  const [type, setType] = useState('bug');
  const [severity, setSeverity] = useState('normal');
  const [description, setDescription] = useState('');
  const [screenshot, setScreenshot] = useState(null);
  const [timeframe, setTimeframe] = useState('immediately');
  const [whatItBlocks, setWhatItBlocks] = useState('');
  const [criticalNature, setCriticalNature] = useState('');
  const [capturing, setCapturing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const resetForm = () => {
    setType('bug');
    setSeverity('normal');
    setDescription('');
    setScreenshot(null);
    setTimeframe('immediately');
    setWhatItBlocks('');
    setCriticalNature('');
    setError(null);
  };

  const captureScreenshot = async () => {
    setCapturing(true);
    setIsOpen(false);
    try {
      // Let the panel close before capturing so it isn't in the shot
      await new Promise((resolve) => setTimeout(resolve, 250));
      const { default: html2canvas } = await import('html2canvas');
      const canvas = await html2canvas(document.body, { scale: 0.75, logging: false, useCORS: true });
      setScreenshot(canvas.toDataURL('image/png'));
    } catch (err) {
      console.error('[Feedback] Screenshot capture failed:', err);
      setError('Screenshot capture failed — you can still submit without one.');
    } finally {
      setCapturing(false);
      setIsOpen(true);
    }
  };

  const handleSubmit = async () => {
    if (!description.trim()) {
      setError('Please describe the issue.');
      return;
    }
    if (severity === 'blocking' && (!whatItBlocks.trim() || !criticalNature.trim())) {
      setError('For blocking issues, fill in what it blocks and why it is critical.');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const response = await authFetch('/api/user-feedback', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type,
          severity,
          pageUrl: location.pathname,
          description: description.trim(),
          screenshotUrl: screenshot,
          blockingDetails: severity === 'blocking'
            ? { timeframe, whatItBlocks: whatItBlocks.trim(), criticalNature: criticalNature.trim() }
            : null
        })
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.error || 'Failed to submit feedback');
      }
      setSubmitted(true);
      trackEvent('feedback_submitted');
      setTimeout(() => {
        setSubmitted(false);
        setIsOpen(false);
        resetForm();
      }, 1800);
    } catch (err) {
      console.error('[Feedback] Submit failed:', err);
      setError(err.message || 'Failed to submit feedback');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* Floating trigger */}
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-5 right-5 z-40 hidden lg:flex items-center gap-2 px-3.5 py-2.5 rounded-full bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium shadow-lg transition-colors"
        title="Send feedback"
      >
        <MessageSquarePlus className="w-4 h-4" />
        <span className="hidden sm:inline">Feedback</span>
      </button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 16 }}
            transition={{ duration: 0.15 }}
            className="fixed bottom-20 right-5 z-50 w-[380px] max-w-[calc(100vw-2.5rem)] bg-card border border-border rounded-xl shadow-2xl overflow-hidden"
          >
            {submitted ? (
              <div className="flex flex-col items-center justify-center py-12 px-6 text-center">
                <CheckCircle className="w-10 h-10 text-green-500 mb-3" />
                <p className="text-sm font-medium text-foreground">Thanks for the feedback!</p>
                <p className="text-xs text-muted-foreground mt-1">The Sell Every Call team will see this shortly.</p>
              </div>
            ) : (
              <div className="flex flex-col max-h-[75vh]">
                <div className="flex items-center justify-between px-4 py-3 border-b border-border">
                  <h3 className="text-sm font-semibold text-foreground">Send feedback</h3>
                  <button
                    onClick={() => setIsOpen(false)}
                    className="p-1 text-muted-foreground hover:text-foreground"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>

                <div className="p-4 space-y-4 overflow-y-auto">
                  {/* Type */}
                  <div className="grid grid-cols-3 gap-2">
                    {TYPES.map((t) => (
                      <button
                        key={t.value}
                        onClick={() => setType(t.value)}
                        className={`flex flex-col items-center gap-1 py-2.5 rounded-lg border text-xs transition-colors ${
                          type === t.value
                            ? 'border-primary-500 bg-accent text-foreground font-medium'
                            : 'border-border text-muted-foreground hover:bg-accent'
                        }`}
                      >
                        <t.icon className="w-4 h-4" />
                        {t.label}
                      </button>
                    ))}
                  </div>

                  {/* Severity */}
                  <div>
                    <label className="block text-xs font-medium text-muted-foreground mb-1">Severity</label>
                    <select
                      value={severity}
                      onChange={(e) => setSeverity(e.target.value)}
                      className="w-full px-3 py-2 bg-background border border-input text-foreground rounded-md text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    >
                      {SEVERITIES.map((s) => (
                        <option key={s.value} value={s.value}>{s.label}</option>
                      ))}
                    </select>
                  </div>

                  {/* Blocking details */}
                  {severity === 'blocking' && (
                    <div className="space-y-3 p-3 rounded-lg border border-red-500/40 bg-red-500/5">
                      <div>
                        <label className="block text-xs font-medium text-muted-foreground mb-1">How soon do you need this?</label>
                        <select
                          value={timeframe}
                          onChange={(e) => setTimeframe(e.target.value)}
                          className="w-full px-3 py-2 bg-background border border-input text-foreground rounded-md text-sm"
                        >
                          {TIMEFRAMES.map((t) => (
                            <option key={t.value} value={t.value}>{t.label}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-muted-foreground mb-1">What does it block?</label>
                        <textarea
                          value={whatItBlocks}
                          onChange={(e) => setWhatItBlocks(e.target.value)}
                          rows={2}
                          className="w-full px-3 py-2 bg-background border border-input text-foreground rounded-md text-sm resize-none"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-muted-foreground mb-1">Why is it critical?</label>
                        <textarea
                          value={criticalNature}
                          onChange={(e) => setCriticalNature(e.target.value)}
                          rows={2}
                          className="w-full px-3 py-2 bg-background border border-input text-foreground rounded-md text-sm resize-none"
                        />
                      </div>
                    </div>
                  )}

                  {/* Description */}
                  <div>
                    <label className="block text-xs font-medium text-muted-foreground mb-1">Description</label>
                    <textarea
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      rows={4}
                      placeholder="What happened? What did you expect?"
                      className="w-full px-3 py-2 bg-background border border-input text-foreground rounded-md text-sm resize-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    />
                  </div>

                  {/* Screenshot */}
                  {screenshot ? (
                    <div className="relative">
                      <img src={screenshot} alt="Screenshot" className="w-full rounded-md border border-border" />
                      <button
                        onClick={() => setScreenshot(null)}
                        className="absolute top-2 right-2 p-1.5 rounded-md bg-black/60 text-white hover:bg-black/80"
                        title="Remove screenshot"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={captureScreenshot}
                      disabled={capturing}
                      className="flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {capturing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Camera className="w-3.5 h-3.5" />}
                      {capturing ? 'Capturing…' : 'Attach a screenshot of this page'}
                    </button>
                  )}

                  {error && <p className="text-xs text-red-500">{error}</p>}

                  <p className="text-[11px] text-muted-foreground">Page: {location.pathname}</p>
                </div>

                <div className="px-4 py-3 border-t border-border">
                  <button
                    onClick={handleSubmit}
                    disabled={submitting}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 disabled:opacity-60 text-white rounded-md text-sm font-medium transition-colors"
                  >
                    {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
                    {submitting ? 'Sending…' : 'Send feedback'}
                  </button>
                </div>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

export default FeedbackWidget;
